import React, { useEffect, useState, useMemo } from "react";
import { useDebounce } from "use-debounce";

import KitiRecharge from "components/Panel/Admin/KitiRecharge";
import { useDispatch, useSelector } from "react-redux";
import { getKitiRecharges } from "redux/admin/kiti-recharge/kitiRechargeSlice";
import Pagination from "components/Pagination";
import moment from "moment";

const KitiRechargeContainer = () => {
  const dispatch = useDispatch();
  const { kitiRecharges: data, isLoading } = useSelector((state) => state.adminKitiRecharges);

  // Filter states
  const [search, setSearch] = useState("");
  const [minAmount, setMinAmount] = useState("");
  const [maxAmount, setMaxAmount] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [perPageSelection, setPerPageSelection] = useState(30);
  const [currentPage, setCurrentPage] = useState(1);
  
  // Debounced search
  const [debouncedSearch] = useDebounce(search, 500);

  // Load initial data
  useEffect(() => {
    dispatch(getKitiRecharges());
  }, [dispatch]);

  // Apply filters and pagination locally
  const filteredData = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];

    let filtered = [...data];

    // Search filter
    if (debouncedSearch) {
      filtered = filtered.filter(item =>
        item.user.toLowerCase().includes(debouncedSearch.toLowerCase())
      );
    }

    // Amount filters
    if (minAmount) {
      const min = parseFloat(minAmount);
      filtered = filtered.filter(item => item.amount >= min);
    }

    if (maxAmount) {
      const max = parseFloat(maxAmount);
      filtered = filtered.filter(item => item.amount <= max);
    }

    // Date filters
    if (startDate) {
      filtered = filtered.filter(item =>
        moment(item.date).isSameOrAfter(moment(startDate), 'day')
      );
    }

    if (endDate) {
      filtered = filtered.filter(item =>
        moment(item.date).isSameOrBefore(moment(endDate), 'day')
      );
    }

    return filtered;
  }, [data, debouncedSearch, minAmount, maxAmount, startDate, endDate]);

  // Pagination logic
  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / perPageSelection);
  const startIndex = (currentPage - 1) * perPageSelection;
  const endIndex = startIndex + perPageSelection;
  const paginatedData = filteredData.slice(startIndex, endIndex);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, minAmount, maxAmount, startDate, endDate, perPageSelection]);

  return (
    <div className="bg-black min-h-screen">
      <div className="p-4 flex justify-between">
        <h2 className="text-xl font-semibold text-white-100">
          Kiti Yüklemeler
        </h2>
      </div>

      {/* Filter Controls */}
      <div className="px-4 pb-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
          {/* Search Box */}
          <div>
            <label className="block text-sm text-white-200 mb-1">
              Kullanıcı Ara
            </label>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              type="text"
              className="w-full border border-gray-500 rounded-lg p-2 bg-gray-800 text-white-100"
              placeholder="Kullanıcı adı..."
            />
          </div>

          {/* Min Amount */}
          <div>
            <label className="block text-sm text-white-200 mb-1">
              Min Tutar
            </label>
            <input
              value={minAmount}
              onChange={(e) => setMinAmount(e.target.value)}
              type="number"
              step="0.01"
              className="w-full border border-gray-500 rounded-lg p-2 bg-gray-800 text-white-100"
              placeholder="0.00"
            />
          </div>

          {/* Max Amount */}
          <div>
            <label className="block text-sm text-white-200 mb-1">
              Max Tutar
            </label>
            <input
              value={maxAmount}
              onChange={(e) => setMaxAmount(e.target.value)}
              type="number"
              step="0.01"
              className="w-full border border-gray-500 rounded-lg p-2 bg-gray-800 text-white-100"
              placeholder="0.00"
            />
          </div>

          {/* Start Date */}
          <div>
            <label className="block text-sm text-white-200 mb-1">
              Başlangıç Tarihi
            </label>
            <input
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              type="date"
              className="w-full border border-gray-500 rounded-lg p-2 bg-gray-800 text-white-100"
            />
          </div>

          {/* End Date */}
          <div>
            <label className="block text-sm text-white-200 mb-1">
              Bitiş Tarihi
            </label>
            <input
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              type="date"
              className="w-full border border-gray-500 rounded-lg p-2 bg-gray-800 text-white-100"
            />
          </div>
        </div>

        {/* Per Page Selection and Stats */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <label className="text-sm text-white-200">
              Sayfa başına:
            </label>
            <select
              value={perPageSelection}
              onChange={(e) => setPerPageSelection(parseInt(e.target.value))}
              className="border border-gray-500 rounded-lg p-2 bg-gray-800 text-white-100"
            >
              <option value={15}>15</option>
              <option value={30}>30</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
              <option value={300}>300</option>
            </select>
          </div>
          
          <div className="text-sm text-white-200">
            Toplam: {totalItems} kayıt
          </div>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="text-center py-8">
          <div className="text-white-200 text-lg">Yükleniyor...</div>
        </div>
      )}

      {/* Data Display */}
      {!isLoading && (
        <>
          <KitiRecharge data={paginatedData} />
          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          )}
        </>
      )}
    </div>
  );
};

export default KitiRechargeContainer;